import { useLayoutEffect, useRef, useState } from "react";

export const LIST_ITEM_STAGGER_MS = 32;
export const LIST_ITEM_STAGGER_CAP = 10;
const LIST_ITEM_REVEAL_MS = 220;
const REVEAL_CLEANUP_BUFFER_MS = 40;

const EMPTY_REVEAL_MAP: ReadonlyMap<string, number> = new Map();

const revealCleanupMs =
  LIST_ITEM_STAGGER_CAP * LIST_ITEM_STAGGER_MS +
  LIST_ITEM_REVEAL_MS +
  REVEAL_CLEANUP_BUFFER_MS;

type UseListItemRevealOptions = {
  /**
   * Changes when the list identity resets (e.g. date filters). Warm cache
   * swaps between non-empty lists skip re-stagger; leaving an empty state
   * still gets a smooth reveal.
   */
  listIdentity?: string;
};

/**
 * Returns per-item CSS animation delays for keys that have not been revealed yet.
 * Supports initial load and incremental pagination without re-animating prior rows.
 *
 * Uses useLayoutEffect so delays exist before paint — avoids a full-opacity flash
 * then opacity:0 restart. Marks keys seen immediately so Strict Mode / refetch
 * effect re-runs do not re-stagger the same rows.
 */
export function useListItemReveal(
  itemKeys: readonly string[],
  isInitialLoad: boolean,
  isEmpty: boolean,
  options: UseListItemRevealOptions = {}
): ReadonlyMap<string, number> {
  const { listIdentity = "" } = options;
  const seenKeysRef = useRef(new Set<string>());
  const prevIdentityRef = useRef(listIdentity);
  const wasEmptyRef = useRef(isEmpty);
  // Warm cache mount: data present on first commit — show instantly, no stagger
  const hadInitialSkeletonRef = useRef(isInitialLoad);
  const warmSeededRef = useRef(false);
  const [revealingDelays, setRevealingDelays] =
    useState<ReadonlyMap<string, number>>(EMPTY_REVEAL_MAP);

  const itemKeysSignature = itemKeys.join("\0");

  useLayoutEffect(() => {
    if (isInitialLoad) {
      hadInitialSkeletonRef.current = true;
      warmSeededRef.current = false;
    }

    const identityChanged = prevIdentityRef.current !== listIdentity;
    const leavingEmpty = wasEmptyRef.current && !isEmpty;
    prevIdentityRef.current = listIdentity;
    wasEmptyRef.current = isEmpty;

    if (isInitialLoad) {
      seenKeysRef.current = new Set();
      setRevealingDelays(EMPTY_REVEAL_MAP);
      return;
    }

    if (isEmpty) {
      seenKeysRef.current = new Set();
      setRevealingDelays(EMPTY_REVEAL_MAP);
      return;
    }

    // Mounted onto a warm cache (never showed a skeleton this session):
    // seed current keys once and skip stagger so content does not flash then
    // re-animate. Later key additions (pagination) still fall through below.
    if (!hadInitialSkeletonRef.current && !warmSeededRef.current) {
      warmSeededRef.current = true;
      seenKeysRef.current = new Set(itemKeys);
      setRevealingDelays(EMPTY_REVEAL_MAP);
      return;
    }

    // Filter change onto a warm non-empty cache: keep rows stable — do not
    // re-stagger the whole page when the background refetch settles.
    // Leaving an empty filtered range *does* get the normal reveal below.
    if (identityChanged && !leavingEmpty) {
      seenKeysRef.current = new Set(itemKeys);
      setRevealingDelays(EMPTY_REVEAL_MAP);
      return;
    }

    const newKeys: string[] = [];
    for (const key of itemKeys) {
      if (!seenKeysRef.current.has(key)) {
        newKeys.push(key);
      }
    }

    if (newKeys.length === 0) {
      return;
    }

    // Mark seen immediately so cleanup/re-run (Strict Mode, signature churn)
    // cannot schedule a second stagger for the same keys.
    for (const key of newKeys) {
      seenKeysRef.current.add(key);
    }

    const delays = new Map<string, number>();
    newKeys.forEach((key, index) => {
      delays.set(
        key,
        Math.min(index, LIST_ITEM_STAGGER_CAP) * LIST_ITEM_STAGGER_MS
      );
    });
    setRevealingDelays(delays);

    const timeoutId = window.setTimeout(() => {
      setRevealingDelays(EMPTY_REVEAL_MAP);
    }, revealCleanupMs);

    return () => window.clearTimeout(timeoutId);
    // itemKeysSignature captures list membership/order changes for pagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKeysSignature, isInitialLoad, isEmpty, listIdentity]);

  return revealingDelays;
}
